/**
 * `.param` / `.params` 文本的读写与 diff。
 *
 * 这块代码存在的全部理由是**与 Mission Planner、QGC 互通**：
 * 用户会从 MP 导出参数备份、拿别人的 `.param` 文件刷进自己的飞机、把 QGC 导出的文件
 * 发到群里。所以这里刻意不发明任何私有格式：
 *   - 每行 `NAME VALUE`（逗号分隔的 MP 变体也读得进来）
 *   - `#` / `;` 开头是注释，行尾同样可以跟注释
 *   - 导出不带任何头部说明（MP/QGC 会忽略 `#` 注释，但"多出来的头"是最容易长歪的地方）
 *
 * 导入的健壮性同样重要：一整份文件里有一行写错，**不能**让另外 1600 个参数一起失败。
 * 非法行按行号单独报告，其余照常解析 —— 用户拿到的应该是"第 37 行有问题"，
 * 而不是"文件无法导入"。
 */
import { formatParamValue, paramValuesEqual } from './float';

export interface ParamFileEntry {
  readonly name: string;
  readonly value: number;
}

/** 一行读不出来的原因。`line` 是 1 起的行号，界面直接显示它。 */
export interface ParamFileError {
  readonly line: number;
  /** 出错那一行的原文（截断到 120 字符，避免整份文件被贴进错误提示） */
  readonly text: string;
  readonly reason: string;
}

export interface ParamFile {
  /** 去重后的参数项，保持首次出现的位置（重复项的**值**取最后一次出现） */
  readonly entries: readonly ParamFileEntry[];
  readonly byName: ReadonlyMap<string, number>;
  readonly errors: readonly ParamFileError[];
  /** 被后面的行覆盖掉的参数名（按出现顺序，重复几次就出现几次） */
  readonly overridden: readonly string[];
  readonly commentLines: number;
}

/** 参数名的字符集：ArduPilot 的参数名是 `A-Z0-9_`，且必须以字母或下划线开头 */
const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** `NAME VALUE` 或 `NAME,VALUE`：名字之后要有一个分隔符 */
const SPLIT_PATTERN = /^([^\s,]+)[\s,]+(.*)$/;

const MAX_ERROR_TEXT = 120;

function truncate(text: string): string {
  const trimmed = text.trim();
  return trimmed.length <= MAX_ERROR_TEXT ? trimmed : `${trimmed.slice(0, MAX_ERROR_TEXT)}…`;
}

/** 去掉行尾注释。参数名与数值里都不会出现 `#` / `;`，所以直接截断是安全的。 */
function stripComment(line: string): string {
  let cut = line.length;
  for (const marker of ['#', ';']) {
    const at = line.indexOf(marker);
    if (at >= 0 && at < cut) cut = at;
  }
  return line.slice(0, cut);
}

/**
 * 解析一份参数文件。
 *
 * 空文件、只有注释的文件都返回**零参数且零错误**（新建后还没保存的备份就是这样）。
 */
export function parseParamFile(text: string): ParamFile {
  const errors: ParamFileError[] = [];
  const overridden: string[] = [];
  const order: string[] = [];
  const byName = new Map<string, number>();
  const position = new Map<string, number>();
  let commentLines = 0;
  let entries: ParamFileEntry[] = [];

  if (text.length > 0) {
    // 一次把三种换行都切开：从 Windows 传来的备份是 `\r\n`，
    // 用 `\n` 切会在每行末尾留下 `\r`，而 `\r` 会让 `NAME VALUE\r` 的值解析失败 ——
    // 这恰恰是最常见的一份真实文件（MP 在 Windows 上导出）。
    const lines = text.split(/\r\n|\n|\r/);
    for (let i = 0; i < lines.length; i += 1) {
      const raw = lines[i] ?? '';
      const line = i + 1;
      const body = stripComment(raw).trim();
      if (body.length === 0) {
        if (raw.trim().length > 0) commentLines += 1;
        continue;
      }

      const match = SPLIT_PATTERN.exec(body);
      if (match === null) {
        errors.push({ line, text: truncate(raw), reason: '缺少参数值（每行必须是 NAME VALUE）' });
        continue;
      }
      const name = match[1] ?? '';
      const rest = (match[2] ?? '').trim();
      if (!NAME_PATTERN.test(name)) {
        errors.push({ line, text: truncate(raw), reason: `参数名不合法：${truncate(name)}` });
        continue;
      }
      if (rest.length === 0) {
        errors.push({ line, text: truncate(raw), reason: '缺少参数值' });
        continue;
      }
      // 值后面不该再有东西。默默忽略会掩盖"文件被截断/被合并"这类真实损坏。
      if (/\s/.test(rest)) {
        errors.push({ line, text: truncate(raw), reason: `参数值后面还有多余内容：${truncate(rest)}` });
        continue;
      }
      const value = Number(rest);
      if (!Number.isFinite(value)) {
        errors.push({ line, text: truncate(raw), reason: `参数值不是有限数：${truncate(rest)}` });
        continue;
      }

      const existing = position.get(name);
      if (existing === undefined) {
        position.set(name, entries.length);
        order.push(name);
        entries.push({ name, value });
      } else {
        // 后者覆盖前者（与 MP/QGC 一致），但要记下来 —— 重复项往往意味着
        // 文件是几份备份拼起来的，用户需要知道这件事
        overridden.push(name);
        const previous = entries[existing];
        if (previous !== undefined) entries[existing] = { name, value };
      }
      byName.set(name, value);
    }
  }

  return {
    // 冻结一次，避免调用方（界面）意外改到解析器的中间结果
    entries: Object.freeze(entries),
    byName,
    errors: Object.freeze(errors),
    overridden: Object.freeze(overridden),
    commentLines,
  };
}

export interface FormatParamFileOptions {
  /**
   * 行序。默认按名字升序 —— MP 与 QGC 导出的都是有序的，
   * 无序文件在肉眼 diff 与版本管理里毫无用处。
   */
  readonly order?: 'name' | 'insertion';
  /** 头部注释（会自动加 `# `）。默认不写。 */
  readonly header?: string;
  readonly lineEnding?: '\n' | '\r\n';
  /** 末尾是否补一个换行。默认补（POSIX 文本文件的惯例，diff 工具会因此少一行"\\ No newline"）。 */
  readonly trailingNewline?: boolean;
}

/** 导出成 `.param` 文本。 */
export function formatParamFile(
  entries: readonly ParamFileEntry[],
  options: FormatParamFileOptions = {},
): string {
  const lineEnding = options.lineEnding ?? '\n';
  const rows = options.order === 'insertion' ? [...entries] : [...entries].sort(compareByName);
  const lines: string[] = [];
  if (options.header !== undefined && options.header.length > 0) {
    for (const line of options.header.split(/\r\n|\n|\r/)) lines.push(line.startsWith('#') ? line : `# ${line}`);
  }
  for (const entry of rows) lines.push(`${entry.name} ${formatParamValue(entry.value)}`);
  const text = lines.join(lineEnding);
  if (text.length === 0) return '';
  return (options.trailingNewline ?? true) ? `${text}${lineEnding}` : text;
}

/** 名字比较：参数名是 ASCII，用码位比较的结果与 MP/QGC 的字典序一致，且不依赖 ICU。 */
function compareByName(a: ParamFileEntry, b: ParamFileEntry): number {
  if (a.name === b.name) return 0;
  return a.name < b.name ? -1 : 1;
}

export type ParamDiffKind = 'added' | 'removed' | 'changed';

export interface ParamDiffEntry {
  readonly name: string;
  readonly kind: ParamDiffKind;
  /**
   * 当前机体上的值；`removed`（机体上有、参考里没有）之外都可能为 null。
   * `added` 表示"参考里有而机体上没有"。
   */
  readonly current: number | null;
  /** 参考值（文件值 / 默认值）；`removed` 时为 null。 */
  readonly reference: number | null;
}

export interface ParamDiff {
  readonly entries: readonly ParamDiffEntry[];
  readonly counts: Readonly<Record<ParamDiffKind, number>>;
  /** 两边都有且值相同的条数 —— "只看改动项"的界面靠它显示"其余 N 项无变化" */
  readonly equal: number;
}

export interface ParamDiffOptions {
  /** 只保留某几类结果（界面上的"只看新增/只看修改"） */
  readonly kinds?: readonly ParamDiffKind[];
}

/**
 * 当前机体值 vs 参考值（文件值 / 默认值）。
 *
 * 判等走 float32 容差：从文件读进来的 `0.1` 与机体上的 float32 0.1
 * 如果被当成"不同"，那么每一份备份都会显示出一片虚假的改动。
 */
export function diffParamValues(
  current: ReadonlyMap<string, number>,
  reference: ReadonlyMap<string, number>,
  options: ParamDiffOptions = {},
): ParamDiff {
  const entries: ParamDiffEntry[] = [];
  const counts = { added: 0, removed: 0, changed: 0 };
  let equal = 0;

  for (const [name, referenceValue] of reference) {
    const currentValue = current.get(name);
    if (currentValue === undefined) {
      counts.added += 1;
      entries.push({ name, kind: 'added', current: null, reference: referenceValue });
      continue;
    }
    if (paramValuesEqual(currentValue, referenceValue)) {
      equal += 1;
      continue;
    }
    counts.changed += 1;
    entries.push({ name, kind: 'changed', current: currentValue, reference: referenceValue });
  }

  for (const [name, currentValue] of current) {
    if (reference.has(name)) continue;
    counts.removed += 1;
    entries.push({ name, kind: 'removed', current: currentValue, reference: null });
  }

  entries.sort(compareDiffEntries);
  const kinds = options.kinds;
  const filtered = kinds === undefined ? entries : entries.filter((entry) => kinds.includes(entry.kind));
  return { entries: Object.freeze(filtered), counts: Object.freeze(counts), equal };
}

function compareDiffEntries(a: ParamDiffEntry, b: ParamDiffEntry): number {
  if (a.name === b.name) return 0;
  return a.name < b.name ? -1 : 1;
}

/** diff / 校验能接受的参考值形式 */
export type ParamValueSource =
  | ParamFile
  | readonly ParamFileEntry[]
  | ReadonlyMap<string, number>;

/** 把上面几种形式统一成 name → value。 */
export function paramValueMap(source: ParamValueSource): ReadonlyMap<string, number> {
  if (source instanceof Map) return source;
  if (Array.isArray(source)) {
    const map = new Map<string, number>();
    for (const entry of source as readonly ParamFileEntry[]) map.set(entry.name, entry.value);
    return map;
  }
  return (source as ParamFile).byName;
}
